/**
 * Timezone-safe date utilities for RevOps Compass
 *
 * ## Why this module exists
 *
 * `new Date().toISOString().split('T')[0]` is the single most common source of
 * timezone bugs in JavaScript. `toISOString()` converts to UTC, which means
 * at 11pm Central Time on Jan 15, `toISOString()` returns `2025-01-16T05:00:00Z`
 * — giving you **tomorrow's date** instead of today's.
 *
 * This module provides drop-in replacements that always operate in the
 * organization timezone (Central Time / America/Chicago).
 *
 * ## Usage
 *
 * ```ts
 * import { toDateString, todayString, getWeekStart } from '@/lib/date-utils';
 *
 * // Instead of: date.toISOString().split('T')[0]
 * const dateStr = toDateString(date);
 *
 * // Instead of: new Date().toISOString().split('T')[0]
 * const today = todayString();
 *
 * // Instead of: manually computing Monday
 * const monday = getWeekStart(date);
 * ```
 *
 * ## ESLint enforcement
 *
 * The `no-restricted-syntax` rule in eslint.config.js will warn on any usage of
 * `.toISOString().split(...)` to catch the dangerous pattern at lint time.
 *
 * @module date-utils
 */

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * The canonical timezone for this organization.
 * All "today", "this week", "this month" calculations use this timezone.
 */
export const ORG_TIMEZONE = 'America/Chicago';

// ---------------------------------------------------------------------------
// Core: Date → "YYYY-MM-DD" string (timezone-safe)
// ---------------------------------------------------------------------------

/**
 * Format a Date as "YYYY-MM-DD" in the organization timezone.
 *
 * This is the **safe replacement** for `date.toISOString().split('T')[0]`.
 *
 * @example
 * // At 11pm Central on Jan 15:
 * toDateString(new Date())  // → "2025-01-15" (correct)
 * // vs toISOString() which would give "2025-01-16" (wrong!)
 */
export function toDateString(date: Date, timezone: string = ORG_TIMEZONE): string {
  const year = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric' }).format(date);
  const month = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, month: '2-digit' }).format(date);
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, day: '2-digit' }).format(date);
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date as "YYYY-MM-DD" in the organization timezone.
 *
 * Drop-in replacement for `new Date().toISOString().split('T')[0]`.
 */
export function todayString(timezone: string = ORG_TIMEZONE): string {
  return toDateString(new Date(), timezone);
}

// ---------------------------------------------------------------------------
// Week boundaries
// ---------------------------------------------------------------------------

/**
 * Get the Monday (start of week) for a given date, as "YYYY-MM-DD".
 *
 * Uses the organization timezone to determine "what day is it", then
 * walks back to Monday.
 *
 * @example
 * getWeekStart(new Date('2026-02-11T12:00:00'))  // Wednesday → "2026-02-09"
 */
export function getWeekStart(date: Date = new Date(), timezone: string = ORG_TIMEZONE): string {
  // Get the day-of-week in the target timezone (0=Sun, 1=Mon, ..., 6=Sat)
  const dayOfWeek = getDayOfWeek(date, timezone);
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const monday = new Date(date);
  monday.setDate(monday.getDate() - daysToSubtract);
  return toDateString(monday, timezone);
}

/**
 * Get the Sunday (end of week) for a given date, as "YYYY-MM-DD".
 */
export function getWeekEnd(date: Date = new Date(), timezone: string = ORG_TIMEZONE): string {
  const dayOfWeek = getDayOfWeek(date, timezone);
  const daysToAdd = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;

  const sunday = new Date(date);
  sunday.setDate(sunday.getDate() + daysToAdd);
  return toDateString(sunday, timezone);
}

// ---------------------------------------------------------------------------
// Month boundaries
// ---------------------------------------------------------------------------

/**
 * Get the first day of the month for a given date, as "YYYY-MM-DD".
 */
export function getMonthStart(date: Date = new Date(), timezone: string = ORG_TIMEZONE): string {
  const parts = getDateParts(date, timezone);
  return `${parts.year}-${parts.month}-01`;
}

/**
 * Get the last day of the month for a given date, as "YYYY-MM-DD".
 */
export function getMonthEnd(date: Date = new Date(), timezone: string = ORG_TIMEZONE): string {
  const parts = getDateParts(date, timezone);
  const lastDay = new Date(parseInt(parts.year), parseInt(parts.month), 0).getDate();
  return `${parts.year}-${parts.month}-${String(lastDay).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Date arithmetic
// ---------------------------------------------------------------------------

/**
 * Add (or subtract) days from a date, returning "YYYY-MM-DD".
 */
export function addDays(date: Date, days: number, timezone: string = ORG_TIMEZONE): string {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return toDateString(result, timezone);
}

/**
 * Get a Date object for N weeks ago from the given date.
 */
export function weeksAgo(n: number, from: Date = new Date()): Date {
  const result = new Date(from);
  result.setDate(result.getDate() - n * 7);
  return result;
}

// ---------------------------------------------------------------------------
// Day-of-week helpers
// ---------------------------------------------------------------------------

/**
 * Get the day of week (0=Sunday, 6=Saturday) in the given timezone.
 *
 * This avoids the bug where `new Date().getDay()` uses the JS engine's
 * local timezone, which may not match the org timezone.
 */
export function getDayOfWeek(date: Date, timezone: string = ORG_TIMEZONE): number {
  // weekday: 'narrow' gives us a single letter, but we need the numeric value.
  // Using Intl to get the weekday name and mapping it is fragile.
  // Instead, we get the date parts in the target timezone and construct a local Date.
  const parts = getDateParts(date, timezone);
  const localEquiv = new Date(parseInt(parts.year), parseInt(parts.month) - 1, parseInt(parts.day));
  return localEquiv.getDay();
}

// ---------------------------------------------------------------------------
// UTC boundary helpers (for database queries)
// ---------------------------------------------------------------------------

/**
 * Convert a "YYYY-MM-DD" date string (in org timezone) to the UTC ISO string
 * representing midnight of that day in the org timezone.
 *
 * Useful for `.gte('created_at', startOfDayUTC('2025-01-15'))` queries.
 */
export function startOfDayUTC(dateStr: string, timezone: string = ORG_TIMEZONE): string {
  // Create a date at midnight in the target timezone
  const date = new Date(`${dateStr}T00:00:00`);
  // Get the offset for this date in the target timezone
  const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' });
  const tzStr = date.toLocaleString('en-US', { timeZone: timezone });
  const utcDate = new Date(utcStr);
  const tzDate = new Date(tzStr);
  const offsetMs = utcDate.getTime() - tzDate.getTime();

  const result = new Date(date.getTime() + offsetMs);
  return result.toISOString();
}

/**
 * Convert a "YYYY-MM-DD" date string (in org timezone) to the UTC ISO string
 * representing 23:59:59.999 of that day in the org timezone.
 */
export function endOfDayUTC(dateStr: string, timezone: string = ORG_TIMEZONE): string {
  const date = new Date(`${dateStr}T23:59:59.999`);
  const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' });
  const tzStr = date.toLocaleString('en-US', { timeZone: timezone });
  const utcDate = new Date(utcStr);
  const tzDate = new Date(tzStr);
  const offsetMs = utcDate.getTime() - tzDate.getTime();

  const result = new Date(date.getTime() + offsetMs);
  return result.toISOString();
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface DateParts {
  year: string;
  month: string;
  day: string;
}

/**
 * Extract year/month/day parts from a Date in a given timezone.
 * Returns zero-padded strings.
 */
function getDateParts(date: Date, timezone: string): DateParts {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // en-CA locale gives "YYYY-MM-DD" format
  const formatted = formatter.format(date);
  const [year, month, day] = formatted.split('-');
  return { year, month, day };
}
